import axios from 'axios';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { NsFormGroup } from '@britishgasenergy/react-nucleus/dist';

import { NsForm } from 'components/common/ns-form/NsForm';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { NsPostcodeInput } from 'components/common/ns-postcode-input/NsPostcodeInput';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';

import { useTariffInfo } from '../hooks/useTariffInfo';

import {
  PAYMENT_METHODS,
  PAYMENT_NAMES,
  FUEL_OPTIONS
} from 'config/app-config';
import { isValidField } from 'utils/fieldValidations';

import {
  setPostcode,
  setPaymentMethod,
  setFuelType,
  resetTariffDetails,
  resetTariffList,
  resetCurrentTariff,
  selectTariffListErrors
} from 'state/tariff-information/tariff-information-slice';
import { getTariffListThunk } from 'state/tariff-information/tariff-information-thunks';
import { Error } from 'components/common/error/Error';

const fuelTypeOptions = [
  {
    label: 'Gas & electricity',
    value: FUEL_OPTIONS.DUAL
  },
  {
    label: 'Electricity',
    value: FUEL_OPTIONS.ELECTRIC
  },
  {
    label: 'Gas',
    value: FUEL_OPTIONS.GAS
  }
];
const paymentMethodOptions = [
  {
    label: PAYMENT_NAMES[PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT],
    value: PAYMENT_METHODS.MONTHLY_DIRECT_DEBIT
  },
  {
    label: PAYMENT_NAMES[PAYMENT_METHODS.PREPAY],
    value: PAYMENT_METHODS.PREPAY
  },
  {
    label: PAYMENT_NAMES[PAYMENT_METHODS.CHECK],
    value: PAYMENT_METHODS.CHECK
  }
];

export const tariffInputTestId = 'tariffInputTestId';
export const tariffInputPostcodeTestId = 'tariffInputPostcodeTestId';

export const TariffInput = () => {
  const dispatch = useDispatch();
  const errors = useSelector(selectTariffListErrors, shallowEqual);
  const {
    fuel,
    postcode,
    paymentMethod,
    isLoadingTariffList
  } = useTariffInfo();

  const handleViewMatchingTariffs = async (event) => {
    event.preventDefault();
    const source = axios.CancelToken.source();
    const requestConfig = { cancelToken: source.token };
    try {
      resetDetails();
      await dispatch(
        getTariffListThunk({ requestConfig, fuel, postcode, paymentMethod })
      );
    } catch (e) {
      e && console.error(e);
    }
  };

  const handlePostcodeChange = (event) => {
    const {
      target: { value }
    } = event;
    const isValid = isValidField(value, 'postcode');
    resetDetails();
    if (isValid) {
      dispatch(setPostcode(value));
    }
  };
  const handlePayementTypeChange = (event) => {
    const {
      target: { value }
    } = event;
    resetDetails();
    dispatch(setPaymentMethod(value));
  };

  const handleFuelTypeChange = (event) => {
    const {
      target: { value }
    } = event;
    resetDetails();
    dispatch(setFuelType(value));
  };

  const resetDetails = () => {
    dispatch(resetCurrentTariff());
    dispatch(resetTariffDetails());
    dispatch(resetTariffList());
  };

  return (
    <NsForm testId={tariffInputTestId} onSubmit={handleViewMatchingTariffs}>
      <NsFormGroup legend="Please fill in your details">
        <NsPostcodeInput
          testId={tariffInputPostcodeTestId}
          onChange={handlePostcodeChange}
          label="What's your postcode?"
          value={postcode}
        />
        <NsRadioInput
          heading="What type of fuel?"
          className="inline"
          name="fuelType"
          dataTestId="tariffFuelTypeTestId"
          options={fuelTypeOptions}
          onChange={handleFuelTypeChange}
          value={fuel}
        />
        <NsRadioInput
          heading="What type of payment method?"
          className="inline"
          name="paymentMethod"
          dataTestId="tariffpaymentMethodTestId"
          options={paymentMethodOptions}
          onChange={handlePayementTypeChange}
          value={paymentMethod}
        />
      </NsFormGroup>
      <Error errors={errors} />
      <NsPrimaryCta isLoading={isLoadingTariffList}>
        View matching tariffs
      </NsPrimaryCta>
    </NsForm>
  );
};
